import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import {
  AutoComplete, Button, Form, Input,
} from 'antd';
import type { AutoCompleteProps } from 'antd';

import { Option } from '../../../../types';
import { Coin, Search } from '../../../Common/Icon';
import { SearchData, useSearch } from '../../../../hooks/api/search';

import styles from './index.module.scss';

interface SearchFormValues {
  search: string;
}

function TippingConfusion() {
  const [form] = Form.useForm<SearchFormValues>();
  const search = useSearch();
  const navigate = useNavigate();

  const [options, setOptions] = useState<AutoCompleteProps['options']>([]);
  const searchValue = Form.useWatch('search', form) ?? '';
  const [loadingSearch, setLoadingSearch] = useState(false);

  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const timeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getSearshData = (value: string) => {
    if (!value || value.trim().length < 3) {
      setOptions([]);

      return;
    }

    setLoadingSearch(true);
    search
      .fetch({ page: 1, pageSize: 10, search: value })
      .then((res: SearchData | null) => {
        if (res) {
          setOptions(normalizeDataForAutocomplete(res));
        } else {
          setOptions([]);
        }
      })
      .catch((error) => {
        console.error('Autocomplete fetch error:', error);
        setOptions([]);
      })
      .finally(() => {
        setLoadingSearch(false);
      });
  };

  // Debounced search
  useEffect(() => {
    if (timeoutId.current) clearTimeout(timeoutId.current);

    const v = searchValue.trim();

    if (v.length < 3) {
      setOptions([]);
      setLoadingSearch(false);
      // Close dropdown if input is cleared
      setOpen(false);

      return;
    }

    setLoadingSearch(true);
    timeoutId.current = setTimeout(() => {
      getSearshData(v);
      setOpen(true); // Open dropdown once query is valid
    }, 700);

    // eslint-disable-next-line consistent-return
    return () => {
      if (timeoutId.current) clearTimeout(timeoutId.current);
    };
  }, [searchValue]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const clickedInsideWrapper = wrapperRef.current?.contains(target);
      const clickedInsidePopup = !!target.closest('.search-autocomplete');

      if (!clickedInsideWrapper && !clickedInsidePopup) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const closeAndReset = () => {
    setOpen(false);
    setOptions([]);
    // Safely reset input via form
    form.setFieldsValue({ search: '' });
  };

  const onFinish = (values: SearchFormValues) => {
    const v = (values.search ?? '').trim();

    if (v.length < 3) {
      setOptions([]);
      setOpen(false);

      return;
    }
    getSearshData(v);
    setOpen(true);
  };

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <div className={styles.coinWrapper}>
        <Coin className={styles.coin1} />
        <Coin className={styles.coin2} />
        <Coin className={styles.coin3} />
        <Coin className={styles.coin4} />
        <Coin className={styles.coin5} />
        <Coin className={styles.coin6} />
        <Coin className={styles.coin7} />
      </div>

      <div className={styles.container}>
        <div className={styles.content}>
          <h2 className={styles.title}>Tired of Tipping Culture?</h2>
          <p className={styles.description}>
            <span>
              Join the conversation and help shape the future of tipping in
              America.
            </span>
            <span>
              {/* eslint-disable-next-line react/no-unescaped-entities */}
              Real customers and tipped workers come together to decide what's fair!
            </span>
          </p>

          <Form<SearchFormValues>
            className={styles.searchWrapper}
            onFinish={onFinish}
            form={form}
          >
            <Form.Item name="search" className={styles.searchFormItem}>
              <AutoComplete
                options={options}
                open={open && searchValue.trim().length >= 3}
                notFoundContent={
                  // eslint-disable-next-line no-nested-ternary
                  searchValue.trim().length < 3
                    ? null
                    : (loadingSearch
                      ? 'Loading...'
                      : 'Nothing found')
                }
                onSelect={(_, option: any) => {
                  if (option?.url) navigate(option.url);
                  closeAndReset();
                }}
                // Reopen dropdown if input already has valid value
                onFocus={() => {
                  if (searchValue.trim().length >= 3) setOpen(true);
                }}
                className={styles.autocomplete}
                popupClassName="search-autocomplete"
              >
                <Input
                  className={styles.search}
                  placeholder="Search service or industry"
                  onPressEnter={form.submit}
                  suffix={(
                    <>
                      <Button
                        type="primary"
                        className={clsx(styles.searchBtn, styles.md)}
                        onClick={form.submit}
                      >
                        <Search /> Search
                      </Button>
                      <Button
                        type="primary"
                        className={clsx(styles.searchBtn, styles.sm)}
                        onClick={form.submit}
                      >
                        <Search />
                      </Button>
                    </>
                  )}
                  allowClear
                  onChange={(e) => {
                    // Keep form value in sync with Input (allowClear compatibility)
                    form.setFieldsValue({ search: e.target.value });
                  }}
                />
              </AutoComplete>
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  );
}

export default TippingConfusion;

const normalizeDataForAutocomplete = (
  apiData: SearchData,
): AutoCompleteProps['options'] => {
  const groups: { label: string; options: Option[] }[] = [];

  if (apiData.business.data.length > 0) {
    groups.push({
      label: 'Businesses',
      options: apiData.business.data.map((b) => ({
        value: b.id,
        label: b.name,
        url: `/business/${b.id}`,
      })),
    });
  }

  if (apiData.categories.data.length > 0) {
    groups.push({
      label: 'Categories',
      options: apiData.categories.data.map((c) => ({
        value: c.id,
        label: c.name,
        url: `/categories/${c.id}`,
      })),
    });
  }

  if (apiData.subCategories.data.length > 0) {
    groups.push({
      label: 'Subcategories',
      options: apiData.subCategories.data.map((s) => ({
        value: s.id,
        label: (
          <div>
            <div>{s.header}</div>
            {s.searchKeywords ? (
              <div className={styles.keywords}>{s.searchKeywords?.join(', ')}</div>
            ) : null}
          </div>
        ),
        url: `/categories/${s.category.id}/${s.id}`,
      })),
    });
  }

  if (apiData.articles.data.length > 0) {
    groups.push({
      label: 'Articles',
      options: apiData.articles.data.map((a) => ({
        value: a.id,
        label: a.name,
        url: `/tipping-articles/${a.id}`,
      })),
    });
  }

  if (apiData.internationals.data.length > 0) {
    groups.push({
      label: 'Internationals',
      options: apiData.internationals.data.map((i) => ({
        value: i.id,
        label: i.country.name,
        url: `/international/${i.id}`,
      })),
    });
  }

  return groups;
};
